import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Users, GraduationCap, UserCheck, HeartHandshake, MessageCircle, Tag, Video, Target, ChevronRight, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { membershipBenefits } from '../data/mock';

const iconMap = {
  Calendar: Calendar,
  Users: Users,
  GraduationCap: GraduationCap,
  UserCheck: UserCheck,
  HeartHandshake: HeartHandshake,
  MessageCircle: MessageCircle,
  Tag: Tag,
  Video: Video,
};

const benefitColors = [
  { bg: 'from-brand-gold to-gold-600', shadow: 'shadow-gold-500/20' },
  { bg: 'from-brand-green to-green-600', shadow: 'shadow-green-500/20' },
  { bg: 'from-brand-red to-red-600', shadow: 'shadow-red-500/20' },
  { bg: 'from-brand-navy to-navy-700', shadow: 'shadow-navy-500/20' },
];

export default function MembershipPage() {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-navy-50 via-white to-gold-50/30">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-0 w-96 h-96 bg-gold-100/30 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <nav className="flex items-center gap-2 text-sm text-navy-500 mb-6">
              <Link to="/" className="hover:text-brand-green transition-colors">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-navy-900 font-medium">Membership</span>
            </nav>
            
            <h1 className="text-4xl sm:text-5xl font-bold text-navy-900 mb-6">
              Join Our{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-green to-green-600">
                Community
              </span>
            </h1>
            <p className="text-lg text-navy-600 leading-relaxed">
              Joining DC Men provides you with a wealth of resources and networking designed to support your growth and success in Early Childhood Education.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold-50 border border-gold-200 mb-6">
              <Target className="w-4 h-4 text-brand-gold" />
              <span className="text-sm font-medium text-gold-700">Member Benefits</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-900 mb-4">What You'll Get</h2>
            <p className="text-navy-600 text-center mx-auto">Access exclusive resources, professional development opportunities, and a supportive community.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {membershipBenefits.map((benefit, index) => {
              const Icon = iconMap[benefit.icon] || Users;
              const colors = benefitColors[index % benefitColors.length];
              return (
                <Card key={index} className="group border-0 shadow-lg shadow-navy-100 hover:shadow-xl hover:shadow-navy-100/50 transition-all duration-500">
                  <CardContent className="p-6">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colors.bg} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg ${colors.shadow}`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-navy-900 mb-2">{benefit.title}</h3>
                    <p className="text-navy-600 text-sm leading-relaxed">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Join Section */}
      <section className="py-24 bg-gradient-to-br from-brand-green to-green-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.05%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-30" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">Why Join DC Men in ECE?</h2>
              <div className="space-y-4">
                {[
                  'Connect with peers who understand your journey',
                  'Access professional development opportunities',
                  'Receive mentorship from experienced educators',
                  'Be part of a movement changing education',
                  'Advance your career with our support',
                  'Make a lasting impact on children\'s lives'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-green-100">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Theory of Change */}
            <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-4 border border-white/20">
              <img 
                src="/images/theory-of-change-1.png" 
                alt="DC Men in ECE Theory of Change"
                className="w-full h-auto rounded-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-navy-900 mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-lg text-navy-600 mb-10">
            Join our community today and start your journey toward excellence in early childhood education.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/join">
              <Button size="lg" className="bg-brand-gold hover:bg-gold-600 text-navy-900 font-semibold px-8 shadow-xl shadow-gold-500/25">
                Join Now
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/conference">
              <Button size="lg" variant="outline" className="border-2 border-navy-200 text-navy-700 font-semibold px-8 hover:bg-navy-50">
                Attend Our Conference
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
